import '@mescius/wijmo.styles/wijmo.css';

import { BACKSPACE, C, D, ENTER, ESCAPE, F, F2, F4, I, LEFT_ARROW, O, RIGHT_ARROW, SPACE, V, X } from '@angular/cdk/keycodes';
import { ChangeDetectionStrategy, Component, computed, effect, HostListener, inject, input, model, output, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { Layout, LayoutColumn } from '@common/data-models/service/layout';
import { DragEventKeys, FieldDefValidations } from '@common/enums/enums';
import { CustomMergeManager, GridDragTypes } from '@common/utils/grid-utils';
import { addClass, removeClass } from '@mescius/wijmo';
import { WjGridModule } from '@mescius/wijmo.angular2.grid';
import { WjComboBox, WjInputModule, WjMultiSelect, WjPopup } from '@mescius/wijmo.angular2.input';
import { CellRange, CellRangeEventArgs, CellType, FlexGrid, FormatItemEventArgs, HitTestInfo, ICellTemplateContext, Row } from '@mescius/wijmo.grid';
import { DataChangeEventArgs, ImmutabilityProvider } from '@mescius/wijmo.grid.immutable';
import { Store } from '@ngrx/store';
import { GridSearchComponent } from '@src/suite/common/components/grid-search/grid-search';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { RichTextSerializer } from '@src/suite/common/data-models/serializers/serializers';
import { BLACK_LINE_GUID } from '@src/suite/common/enums/constants';
import { NavigationDirection } from '@src/suite/common/enums/navigation-direction';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { formatDurationLikeMoment, isDayDurationFormat } from '@src/suite/common/utils/date-utils';
import { hasManualActualTime } from '@src/suite/common/utils/timing';
import { parseDurationString } from '@src/suite/common/utils/timing-format';
import { isSigned32bitInt, KeyName } from '@src/suite/common/utils/util';
import { selectAutoCreateMosDeviceItems, selectAutoCreateMosDeviceItemsList } from '@src/suite/live/store/selectors/live.selectors';
import { DateTime, Duration, Settings } from 'luxon';

import { IsArrayPipe } from '../../pipes/is-array';
import { GridSearchComponent as GridSearchComponent_1 } from '../grid-search/grid-search';
import { StRoGridTimingBarComponent } from './st-ro-grid-timing-bar/st-ro-grid-timing-bar';

@Component({
    selector: 'st-ro-grid',
    imports: [GridSearchComponent_1, IsArrayPipe, MatIcon, StRoGridTimingBarComponent, WjGridModule, WjInputModule],
    templateUrl: './st-ro-grid.html',
    styleUrl: './st-ro-grid.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-ro-grid',
    },
})
export class StRoGridComponent {
    fieldDefValidations = FieldDefValidations;
    PSTFieldNames = PSTFieldNames;
    NavigationDirection = NavigationDirection;
    private provider: ImmutabilityProvider<StorytellingStoryRecord>;
    globalTablesService = inject(GlobalTablesService);
    store = inject(Store);
    matches: { row: number; col: number }[] = [];
    searchAgain = false;
    previousSearch: string | null = null;
    colPageNum = -1;
    currentMatchIndex = -1;
    currentMatchCell: ICellTemplateContext | null = null;
    ctrlIsDown = false;
    pendingChanges = false;
    initialResize = false;

    autoCreateMosDeviceItems = this.store.selectSignal(selectAutoCreateMosDeviceItems);
    autoCreateMosDeviceItemsList = this.store.selectSignal(selectAutoCreateMosDeviceItemsList)();
    i18n = this.store.selectSignal(selectI18N)();

    readonly itemsSource = input<StorytellingStoryRecord[]>([]);
    readonly record = input.required<StorytellingRundownRecord>();
    readonly gridLayout = input<Layout>();
    readonly selectedRows = model<StorytellingStoryRecord[]>([]);
    readonly currentId = input<string>();
    readonly isReadOnly = model<boolean>(false);
    readonly storiesLockStatus = input<boolean>(false);
    readonly isRundownDisabled = input<boolean>(false);
    readonly isRundownArchived = input<boolean>(false);
    readonly isSearchActive = input<boolean>(false);

    readonly collectionChanged = output<{ propID: string; value: any; rowIndex: number; guid: string }>();
    readonly collectionReordered = output<{ sequence: string[] }>();
    readonly openStoryContent = output<{ guid: string }>();
    readonly deleteStory = output<{ guid: string }>();
    readonly addStoryToRundown = output<{
        scriptDatabase: string;
        scriptPath: string;
        scriptGuid: string;
        insertBeforeGuid: string;
        propertiesToOverride: Record<string, any>;
        forceReloadStory: boolean;
    }>();
    readonly addRowOnCommand = output();
    readonly addRowAbove = output();
    readonly dropedScriptsToCollection = output<{
        stories: Partial<StorytellingStoryRecord>[];
        insertBeforeGuid: string;
        sourceRO: Partial<StorytellingRundownRecord>;
        copyTo: boolean;
    }>();
    readonly toggleSearch = output<void>();
    readonly resizedColumn = output<{ column: LayoutColumn }>();

    readonly flexGrid = viewChild.required<FlexGrid>('flexGrid');
    readonly searchInputComponent = viewChild<GridSearchComponent>('searchInput');
    readonly objectPlaceHolderCreatePopup = viewChild.required<WjPopup>('objectPlaceHolderCreatePopup');

    // the state of ctrl key
    readonly searchTerm = signal('');
    readonly currentObjectPlaceholderCreateCell = signal<ICellTemplateContext | null>(null);

    readonly mergedRowsRange = signal<Record<string, CellRange>>({});
    readonly selectAtPosition = model<{ row: number; colName: string }>(null);
    readonly objectPlaceholderCreatePanelOpen = signal(false);
    readonly mosDevicesCbValue = signal('');
    readonly mosGroupsCbValue = signal('');
    readonly mosTypesCbValue = signal('');
    readonly mosSlugCbValue = signal('');
    /*********************************************************************** */

    readonly usersDataMap = computed(() => {
        return Object.values(this.globalTablesService.Staff);
    });

    readonly objectPlaceholderCreateGroupsItemSource = computed(() => {
        const device = this.autoCreateMosDeviceItems().find(item => item.id === this.mosDevicesCbValue());
        return device?.definedGroups?.length > 1 ? device?.definedGroups : null;
    });

    readonly objectPlaceholderCreateHasGroups = computed(() => {
        return this.objectPlaceholderCreateGroupsItemSource()?.length > 1;
    });

    readonly mosTypes = computed(() => {
        return [
            { id: '', title: '' },
            { id: this.i18n.OPCAudioContentLabel, title: this.i18n.OPCAudioContentLabel },
            { id: this.i18n.OPCStillContentLabel, title: this.i18n.OPCStillContentLabel },
            { id: this.i18n.OPCVideoContentLabel, title: this.i18n.OPCVideoContentLabel },
        ];
    });

    constructor() {
        effect(() => {
            if (this.itemsSource() && this.provider) {
                if (!this.flexGrid()?.editRange) {
                    const collectionChanged = this.itemsSource()?.length !== this.provider.itemsSource?.length;
                    const currentSelection = this.flexGrid()?.selection;
                    const itemsWithoutBlackLines = this.itemsSource().filter(item => item.Guid !== BLACK_LINE_GUID);
                    const changedItems = itemsWithoutBlackLines.filter(item => {
                        for (const providerItem of this.provider.itemsSource) {
                            if (providerItem.Guid === item.Guid && providerItem.RowModTime !== item.RowModTime) {
                                return true;
                            }
                        }
                        return false;
                    });
                    this.provider.itemsSource = this.itemsSource().map(this.process);
                    this.pendingChanges = false;
                    if (changedItems?.length === itemsWithoutBlackLines?.length) {
                        this.flexGrid().autoSizeRows();
                    } else {
                        changedItems.forEach(item => {
                            const row = this.flexGrid().rows.find(rowItem => rowItem.dataItem?.Guid === item.Guid);
                            if (row) {
                                this.flexGrid().autoSizeRow(row.index);
                            }
                        });
                    }
                    if (collectionChanged && this.selectAtPosition()) {
                        this.flexGrid().deferUpdate(() => {
                            const columnIndex = this.findColumnIndex(this.selectAtPosition().colName);
                            this.flexGrid().select(this.selectAtPosition().row, columnIndex);
                            this.selectAtPosition.set(null);
                        });
                    } else {
                        this.flexGrid()?.deferUpdate(() => {
                            this.flexGrid().select(currentSelection, false);
                        });
                    }
                } else {
                    this.pendingChanges = true;
                    this.applyRowFlashing(this.itemsSource());
                }
            }
        });

        effect(() => {
            this.calculateMergeRanges();
        });
        effect(() => {
            if (this.gridLayout()?.columns && !this.flexGrid().editRange) {
                this.flexGrid().autoSizeRows();
            }
        });
    }

    private process = (record: StorytellingStoryRecord): any => {
        const rec = {};
        for (const key in record) {
            const fieldDef = this.globalTablesService.getFieldDef(key);
            if (fieldDef?.Validation?.toUpperCase() === 'COMBOBOX') {
                rec[key] = record[key]?.id as LookupValue;
            } else {
                rec[key] = record[key];
            }
        }
        return rec;
    };

    objectAutoCreateCellTemplate = (ctx: ICellTemplateContext) => {
        const device = this.globalTablesService.MOSDevices.find(val => val.ID === ctx.value);
        return device ? device?.AliasValue || device?.ID : ctx.value;
    };

    objectAutoCreateItemFormatter = (_: number, content: string) => {
        const device = this.globalTablesService.MOSDevices.find(val => val.ID === content);
        return device ? device?.AliasValue || device?.ID : content;
    };

    checkListCellTemplate(ctx: ICellTemplateContext) {
        const value = ctx.value as LookupValue[];
        return value?.map(val => val.title)?.join(', ') || '';
    }

    comboBoxCellTemplate = (ctx: ICellTemplateContext) => {
        return this.globalTablesService.calculateListItemTitle(ctx?.col?.binding, ctx?.value, ctx?.value);
    };

    staffIdListCellTemplate = (ctx: ICellTemplateContext) => {
        if (Array.isArray(ctx.value)) {
            return ctx.value ? ctx.value.map(val => this?.globalTablesService?.Staff[val]?.DisplayName).join(', ') : '';
        }
        return this?.globalTablesService?.Staff[ctx.value]?.DisplayName;
    };

    staffIdListItemFormatter = (_: number, content: string) => {
        return this?.globalTablesService?.Staff[content]?.DisplayName;
    };

    staffIdListHeaderFormatter = (content: WjMultiSelect) => {
        return content.checkedItems?.length > 3
            ? `${content.checkedItems.length} ${this.i18n.PSTWebFlexGridMultiSelectItemsSelected}`
            : content.checkedItems.map(val => this?.globalTablesService?.Staff[val]?.DisplayName).join(', ');
    };

    mosStatusCellTemplate(ctx: ICellTemplateContext) {
        const value = ctx.value;
        if (!value) return '';
        const res = `<div style="display: flex; flex-direction: column">${value
            ?.split('\r\n')
            ?.map(val => `<span class="mos-status" mos-status="${val}">${val}</span>`)
            ?.join(' ')}</div>`;
        return res;
    }

    durationCellTemplate(ctx: ICellTemplateContext) {
        let value = ctx.value;
        // the RO Actual column merges with the read time. rec.Actual is only set for a
        // manually entered Actual, so rows without one show the computed read time.
        if (ctx.col?.binding === PSTFieldNames.Actual && !hasManualActualTime(ctx.item)) {
            value = ctx.item?.ReadTime ?? value;
        }
        if (!value) return '';
        if (Duration.isDuration(value)) {
            return formatDurationLikeMoment(value);
        }
        if (typeof value === 'string') {
            const ret = Duration.fromObject({ seconds: parseFloat(value) });
            return formatDurationLikeMoment(ret);
        }
        return '';
    }

    timeCellTemplate(ctx: ICellTemplateContext) {
        const value: DateTime = ctx.value;
        return value?.toFormat('H:mm:ss') ?? '';
    }

    dateCellTemplate(ctx: ICellTemplateContext) {
        const value: DateTime = ctx.value;
        return value?.toLocaleString(DateTime.DATE_SHORT, { locale: Settings.defaultLocale }) ?? '';
        // return value?.moment?.format(moment.localeData().longDateFormat('L')) || ''; // Left for reference
    }

    onMosTypeChanged(index: number, mosType: WjComboBox) {
        const selectedType = this.mosTypes().find(val => val?.id?.toUpperCase() === this.autoCreateMosDeviceItems[index]?.objectType?.toUpperCase());
        mosType.selectedValue = selectedType?.id || this.mosTypes()[0].id;
    }

    formatItem(grid: FlexGrid, e: FormatItemEventArgs) {
        this.applyAlternatingRowsClass(grid, e);
    }

    calculateMergeRanges() {
        const mergedRowsRange = {};
        const slugIdx = this.flexGrid()?.columns.findIndex(val => val.binding === PSTFieldNames.Slug);
        for (let r = 0; r < this.flexGrid()?.rows.length - 1; r++) {
            const range = this.flexGrid()?.getMergedRange(this.flexGrid()?.cells, r, slugIdx);
            if (range) {
                mergedRowsRange[`${range._col}${range._col2}${range._row}${range.row2}`] = range;
            } else {
                mergedRowsRange[`${slugIdx}${slugIdx}${r}${r}`] = new CellRange(r, slugIdx, r, slugIdx);
            }
        }
        this.mergedRowsRange.set(mergedRowsRange);
    }

    applyAlternatingRowsClass(_grid: FlexGrid, e: FormatItemEventArgs) {
        if (e.panel.cellType === CellType.Cell || e.panel.cellType === CellType.RowHeader) {
            Object.keys(this.mergedRowsRange()).forEach((key, idx) => {
                const range = this.mergedRowsRange()[key];
                if (idx % 2 === 1) {
                    if (range.containsRow(e.row)) {
                        addClass(e.cell, 'alternating-row');
                    }
                }
            });
        }
    }

    createPlaceholder() {
        const value = `${this.mosDevicesCbValue()};${this.mosGroupsCbValue()};${this.mosTypesCbValue()};${this.mosSlugCbValue()}`;
        this.collectionChanged.emit({
            propID: PSTFieldNames.ObjectPlaceholderCreate,
            value: { [PSTFieldNames.ObjectPlaceholderCreate]: value },
            rowIndex: this.currentObjectPlaceholderCreateCell()?.row.index,
            guid: this.currentObjectPlaceholderCreateCell()?.item.Guid,
        });
        this.mosDevicesCbValue.set('');
        this.mosGroupsCbValue.set('');
        this.mosTypesCbValue.set('');
        this.mosSlugCbValue.set('');
        this.currentObjectPlaceholderCreateCell.set(null);
    }

    cancelPlaceholderCreate() {
        this.mosDevicesCbValue.set('');
        this.mosGroupsCbValue.set('');
        this.mosTypesCbValue.set('');
        this.mosSlugCbValue.set('');
        this.currentObjectPlaceholderCreateCell.set(null);
    }

    heightMap = new Map();
    onLoadedRows(grid: FlexGrid) {
        grid.rows.forEach(row => {
            const height = this.heightMap.get(row.dataItem?.Guid);
            if (height != null) {
                row.height = height;
            }
        });

        if (!this.initialResize && grid.rows?.length > 1) {
            grid.autoSizeRows();
            this.initialResize = true;
        }
    }

    onResizedColumn(grid: FlexGrid, e: CellRangeEventArgs) {
        const column = grid.columns[e.col];

        if (column.wordWrap) {
            this.flexGrid().autoSizeRows();
        }

        // Update the layout with the new column width for non-autosized columns
        const layout = this.gridLayout();
        const layoutColumn = layout?.columns?.find(c => c.name === column.binding);
        if (layoutColumn && !layoutColumn.autoSizeWidth) {
            const updatedColumn = {
                ...layoutColumn,
                width: column.width,
                originalWidth: column.width * 15,
            };
            this.resizedColumn.emit({ column: updatedColumn });
        }
    }

    onLoadingRows(grid: FlexGrid) {
        this.heightMap = new Map();
        grid.rows.forEach((row: Row) => {
            this.heightMap.set(row.dataItem?.Guid, row.height);
        });
    }

    initializedGrid(flexGrid: FlexGrid): void {
        const slugIdx = flexGrid.columns.findIndex(val => val.binding === PSTFieldNames.Slug);
        if (!flexGrid) {
            return;
        }
        flexGrid.mergeManager = new CustomMergeManager();
        this.provider = new ImmutabilityProvider(flexGrid, {
            itemsSource: this.itemsSource()?.map(this.process) || [],
            dataChanged: (s: ImmutabilityProvider, e: DataChangeEventArgs) => {
                const columnChanged = s.grid.columns[s.grid.selection.col];
                if (columnChanged) {
                    const mergeRange = s.grid.getMergedRange(s.grid.cells, e.itemIndex, slugIdx);
                    if (columnChanged.binding === PSTFieldNames.Slug && mergeRange) {
                        const oldSlug = e.oldItem?.[PSTFieldNames.Slug];
                        let idxToCompare = mergeRange.topRow;
                        if (e.itemIndex === idxToCompare) {
                            idxToCompare = mergeRange.bottomRow;
                        }

                        if (oldSlug !== s.grid.rows[idxToCompare].dataItem?.[PSTFieldNames.Slug]) {
                            this.collectionChanged.emit({ propID: columnChanged.binding, value: e.newItem, rowIndex: e.itemIndex, guid: flexGrid.rows[e.itemIndex].dataItem.Guid });
                        } else {
                            for (let i = mergeRange._row; i <= mergeRange._row2; i++) {
                                this.collectionChanged.emit({ propID: columnChanged.binding, value: e.newItem, rowIndex: i, guid: flexGrid.rows[i].dataItem.Guid });
                            }
                        }
                    } else {
                        if (this.globalTablesService.isDurationFieldType(columnChanged.binding)) {
                            const inputValue: string = e.newItem[columnChanged.binding];
                            if (typeof inputValue === 'string' && !isDayDurationFormat(inputValue) && inputValue?.includes('.')) {
                                e.newItem[columnChanged.binding] = inputValue.replace(/\./g, ':');
                            }
                        }
                        this.collectionChanged.emit({ propID: columnChanged.binding, value: e.newItem, rowIndex: e.itemIndex, guid: e.newItem.Guid });
                    }
                }
            },
        });
        this.calculateMergeRanges();
        flexGrid.formatItem.addHandler((grid, event) => this.formatItem(grid, event));

        flexGrid.beginningEdit.addHandler((s: FlexGrid, e: CellRangeEventArgs) => {
            if (e?.data?.key === ' ' && this.record()?.OnAir) {
                e.cancel = true;
                return;
            }
            const column = s.columns[e.col];
            if (column?.name === 'delete-column' || e?.data?.key === KeyName.Delete) {
                e.cancel = true;
                return;
            }
        });

        flexGrid.cellEditEnding.addHandler((s: FlexGrid, e: CellRangeEventArgs) => {
            // Check if the value has actually changed; simply entering the cell triggers isActualEnteredManually = true
            const columnName = e?.getColumn()?.binding;
            const newValue: string = s?.activeEditor?.value;

            const isDurationFieldInvalid = () => {
                if (!this.globalTablesService.isDurationFieldType(columnName) || !newValue) return false;
                // Allow MM:SS, MM:S or MM.SS, MM.S, plus HH:MM:SS, HH.MM.SS, HH:MM.SS and HH.MM:SS duration formats
                // e.g. "7:0", "7:00", "7.0", "7.00", "12:30", "12.30", "12:30:45", "12.30.45", "12:30.45", "12.30:45"
                const isClockDurationFormat = /^(?:\d+[:.]\d{1,2}|\d{1,2}[:.]\d{1,2}[:.]\d{1,2})$/.test(newValue);
                if (isClockDurationFormat || isDayDurationFormat(newValue)) return false;
                return isNaN(Number(newValue)) || !isSigned32bitInt(parseInt(newValue));
            };
            if (isDurationFieldInvalid()) {
                e.cancel = true;
            } else if (columnName === PSTFieldNames.Slug) {
                const oldValue = s?.getCellData(e.row, e.col, true);
                if (newValue === oldValue) {
                    e.cancel = true;
                }
            } else if (columnName === PSTFieldNames.Actual || columnName === PSTFieldNames.Estimated) {
                if (newValue) {
                    const newDuration = parseDurationString(newValue);
                    const oldValue: Duration = s?.getCellData(e.row, e.col, false);
                    const oldDuration = Duration.isDuration(oldValue) ? oldValue : Duration.fromObject(oldValue ?? {});
                    const oldSeconds = oldDuration.as('seconds');
                    const newSeconds = newDuration.as('seconds');

                    if (oldSeconds === newSeconds) {
                        e.cancel = true;
                    }
                }
            }
        });

        flexGrid.cellEditEnded.addHandler((_: FlexGrid, e: CellRangeEventArgs) => {
            this.provider.grid.onRowEditEnded(e);
        });

        flexGrid.rowEditEnding.addHandler((_: FlexGrid, e: CellRangeEventArgs) => {
            e.cancel = true;
        });
        flexGrid.rowEditEnded.addHandler((_: FlexGrid, e: CellRangeEventArgs) => {
            if (e.cancel) {
                if (this.pendingChanges) {
                    this.provider.itemsSource = this.itemsSource().map(this.process);
                }
            }
            this.pendingChanges = false;
        });

        flexGrid.itemFormatter = (panel, r, c, cell) => {
            const row = panel.rows[r];
            const rowDataItem = panel.rows[r].dataItem;
            const column = panel.columns[c];
            const onAir = this.record()?.OnAir;
            const isDraggable = !this.isRundownArchived();
            // Enable drag and drop for cells
            if (r === panel.rows?.length || rowDataItem?.Guid === BLACK_LINE_GUID || (!row?.dataItem && panel.cellType === CellType.Cell)) {
                cell.draggable = false;
                row.isReadOnly = true;
                cell.textContent = '';
            } else if (panel.cellType === CellType.Cell) {
                cell.draggable = isDraggable;
            } else if (panel.cellType === CellType.RowHeader) {
                cell.draggable = isDraggable;
            }

            // black line
            if (rowDataItem?.Guid === BLACK_LINE_GUID) {
                panel.rows[r].isReadOnly = true;
                panel.rows[r].allowDragging = false;
                row.isReadOnly = true;
                cell.textContent = '';
                addClass(cell, 'black-line');
                return;
            }

            if (this.globalTablesService.isCheckboxFieldType(column.binding)) {
                const inputEl = cell.querySelector('input');
                if (inputEl) {
                    inputEl.indeterminate = false;
                }
            }

            // story status
            if (rowDataItem) {
                // grid rows should be disabled based on users privileges for stories or if story is locked by another user
                const isStoryDisabled = !rowDataItem.isWriteable;
                const isLockedByOther = rowDataItem?.Locked && !this.isStoryLockedByMe(rowDataItem) && rowDataItem?.LockedBy != null;
                if (isStoryDisabled || isLockedByOther) {
                    row.isReadOnly = true;
                }
                if (column.binding === PSTFieldNames.SegStatus && panel.cellType === CellType.Cell) {
                    if (rowDataItem.Status !== undefined) {
                        if (rowDataItem.Status === '2') {
                            addClass(cell, 'yellow');
                        } else if (rowDataItem.Status === '3') {
                            addClass(cell, 'red');
                        } else if (rowDataItem.Status === '4') {
                            addClass(cell, 'green');
                        } else {
                            removeClass(cell, 'yellow');
                            removeClass(cell, 'red');
                            removeClass(cell, 'green');
                        }
                    }
                }
            }

            if (panel.cellType === CellType.ColumnHeader) {
                if (column.binding === PSTFieldNames.RowStatus || column.binding === PSTFieldNames.SegStatus) {
                    cell.textContent = '';
                }
            }

            // merged cells
            const range = flexGrid.getMergedRange(flexGrid.cells, r, slugIdx);
            if (range) {
                const startRow = range._row;
                const endRow = range._row2;

                // iterate through every merged row
                // if even one row has no content, left col is yellow
                // red if all stories have content
                // green if all stories have content and approved
                let yellow = false;
                let red = false;
                let green = false;
                let transparent = false;
                for (let a = startRow; a <= endRow; a++) {
                    // const flexRow = panel.rows[a];
                    // const index = flexRow.dataIndex;
                    // const guid = flexRow.dataItem.GUID;
                    if (panel.rows[a] !== null && panel.rows[a] !== undefined) {
                        if (panel.rows[a].dataItem !== null && panel.rows[a].dataItem !== undefined) {
                            const rowData = panel.rows[a].dataItem;
                            const status = rowData.Status;
                            if (status === '2') {
                                yellow = true;
                            } else if (status === '3') {
                                if (yellow === true) {
                                    red = false;
                                } else {
                                    red = true;
                                }
                            } else if (!status) {
                                transparent = true;
                            }
                        }
                    }
                }
                if (yellow === false && red === false) {
                    green = true;
                } else {
                    green = false;
                }

                if (column.binding === PSTFieldNames.RowStatus && panel.cellType === CellType.Cell) {
                    //cell.style.borderBottom = "none";
                    if (yellow) {
                        addClass(cell, 'yellow');
                    } else if (red) {
                        addClass(cell, 'red');
                    } else if (transparent) {
                        removeClass(cell, 'yellow');
                        removeClass(cell, 'red');
                        removeClass(cell, 'green');
                    } else if (green) {
                        addClass(cell, 'green');
                    } else {
                        removeClass(cell, 'yellow');
                        removeClass(cell, 'red');
                        removeClass(cell, 'green');
                    }
                }
            } else {
                if (column.binding === PSTFieldNames.RowStatus && panel.cellType === CellType.Cell) {
                    const status = rowDataItem?.Status;
                    if (status === '2') {
                        addClass(cell, 'yellow');
                    } else if (status === '3') {
                        addClass(cell, 'red');
                    } else if (status === '4') {
                        addClass(cell, 'green');
                    } else {
                        removeClass(cell, 'yellow');
                        removeClass(cell, 'red');
                        removeClass(cell, 'green');
                    }
                }
            }

            // break, float, cbreak logic
            if (rowDataItem) {
                const hasBreak = rowDataItem.Break;
                const hasFloat = rowDataItem.Float;
                const hasCBreak = rowDataItem.CBreak;
                if (column.binding !== PSTFieldNames.RowStatus && column.binding !== PSTFieldNames.SegStatus) {
                    if (hasBreak && !(hasFloat || hasCBreak)) {
                        addClass(cell, 'break');
                    } else if (hasBreak && (hasFloat || hasCBreak)) {
                        addClass(cell, 'float-cbreak');
                    } else if (hasFloat || hasCBreak) {
                        addClass(cell, 'float-cbreak');
                    } else {
                        removeClass(cell, 'break');
                        removeClass(cell, 'float-cbreak');
                    }
                }
            }

            if (column.binding === PSTFieldNames.ObjectPlaceholderCreate) {
                addClass(cell, 'object-placeholder-create');
            } else {
                removeClass(cell, 'object-placeholder-create');
            }

            const shouldApplyOnAir = onAir && rowDataItem && rowDataItem?.Guid === this.currentId() && column.binding !== PSTFieldNames.RowStatus && column.binding !== PSTFieldNames.SegStatus;
            if (shouldApplyOnAir) {
                addClass(cell, 'on-air');
            } else {
                removeClass(cell, 'on-air');
            }

            if (this.record()?.FreezePages && column.binding === PSTFieldNames.PageNum) {
                addClass(cell, 'frozen-page-numbers');
            } else {
                removeClass(cell, 'frozen-page-numbers');
            }

            if (hasManualActualTime(rowDataItem) && column.binding === PSTFieldNames.Actual) {
                addClass(cell, 'actual-entered-manually');
            } else {
                removeClass(cell, 'actual-entered-manually');
            }

            if (column.name === 'delete-column') {
                if (rowDataItem?.isDeleted && !row.isReadOnly) {
                    row.isReadOnly = true;
                }
                const shouldBeReadOnly = this.isReadOnly() || this.isRundownDisabled();
                if (column.isReadOnly !== shouldBeReadOnly) {
                    column.isReadOnly = shouldBeReadOnly;
                }
            }
            if (column.binding === PSTFieldNames.PageNum) {
                const shouldBeReadOnly = !this.record()?.FreezePages;
                if (column.isReadOnly !== shouldBeReadOnly) {
                    column.isReadOnly = shouldBeReadOnly;
                }
            }
            if (this.isRundownArchived()) {
                row.isReadOnly = true;
            }
            if (this.isSearchActive() && this.currentMatchIndex !== -1) {
                const rowIndex = row.index;
                const colIndex = column.index;
                const match = this.matches?.[this.currentMatchIndex];
                if (match) {
                    // The editor will use the raw data from rowDataItem.
                    if (this.flexGrid().editRange && (this.flexGrid().editRange.contains(rowIndex, colIndex) || this.flexGrid().editRange.contains(rowIndex, this.colPageNum))) {
                        return;
                    }

                    if (rowIndex === match.row && colIndex === match.col) {
                        this.searchAgain = true;
                        this.applySearchHighlightingToCell(rowIndex, colIndex, cell, rowDataItem, column);
                    }
                }
            }
            if (rowDataItem?.isRowFlashing && !this.flexGrid().editRange?.containsColumn(column.index)) {
                addClass(cell, 'flash');
            } else {
                removeClass(cell, 'flash');
            }
        };

        this._dragDropListeners(flexGrid);
        this._selectionChangedListeners(flexGrid);
        /** drag and drop bug fix ********************************************** */
        // add keyboard events listeners to detect the ctrlkey state in selectionChanging event of the grid
        this._keyboardListeners(flexGrid);
        this._clickListeners(flexGrid);
        /*********************************************************************** */

        this.clearGridSelection(flexGrid);
    }

    objectPlaceHolderCreatePopupCreate(_event: MouseEvent, btnClickNone: HTMLElement, cell: ICellTemplateContext) {
        this.currentObjectPlaceholderCreateCell.set(cell);
        this.objectPlaceHolderCreatePopup().owner = btnClickNone;
        this.objectPlaceHolderCreatePopup().show();
    }

    openStory(cell: ICellTemplateContext): void {
        this.openStoryContent.emit({ guid: cell.item.Guid });
    }

    deleteBasicContent(cell: ICellTemplateContext): void {
        if (cell?.row?.isReadOnly || cell.item.isDeleted) {
            return;
        }
        if (!this.isRundownDisabled()) {
            this.deleteStory.emit({ guid: cell.item.Guid });
        }
    }

    /** drag and drop bug fix ********************************************** */
    private _dragDropListeners(s: FlexGrid) {
        // disable built-in row drag/drop
        s.addEventListener(
            s.hostElement,
            'mousedown',
            (e: MouseEvent) => {
                if (s.hitTest(e).cellType === CellType.RowHeader) {
                    e.stopPropagation();
                }
                if (s.hitTest(e).col === 0) {
                    e.stopPropagation();
                }
            },
            true
        );
        // handle drag start
        s.addEventListener(
            s.hostElement,
            'dragstart',
            (e: DragEvent) => {
                const ht = s.hitTest(e);
                if (this.isReadOnly() || this.isRundownDisabled() || this.flexGrid().editRange || ht.cellType === CellType.ColumnHeader) {
                    e.preventDefault();
                    return false;
                }
                if ((ht.cellType === CellType.RowHeader || ht.cellType === CellType.Cell) && ht.row < s.rows.length - 1) {
                    const item = ht.getRow().dataItem;
                    if (!item || item.Guid === BLACK_LINE_GUID) {
                        return;
                    }
                    _createImage(s);
                    // collect row idnexes for drop event different from black line
                    let selectedRows = s.selectedRows.filter(v => !!v.dataItem?.Guid && v.dataItem?.Guid !== BLACK_LINE_GUID);

                    const dragRowIsSelected = selectedRows.some(v => v.dataItem?.Guid === item.Guid);
                    if (!dragRowIsSelected) {
                        selectedRows = [
                            {
                                dataItem: item,
                            } as unknown as Row,
                        ];
                    }
                    const stories = selectedRows.map(val => {
                        return {
                            Guid: val.dataItem.Guid,
                            Database: val.dataItem.Database,
                            Path: val.dataItem.Path,
                            Slug: val.dataItem?.Slug,
                            Segment: val.dataItem?.Segment,
                            Approved: val.dataItem?.[PSTFieldNames.Approved],
                        };
                    });

                    if (stories.length === 0) {
                        return;
                    }
                    e.dataTransfer!.effectAllowed = 'move';
                    e.dataTransfer!.setData(
                        DragEventKeys.DRAG_STORY,
                        JSON.stringify({
                            actionType: GridDragTypes.REORDER.toString(),
                            sourceRO: {
                                Guid: this.record()?.Guid,
                                Database: this.record()?.Database,
                                Path: this.record()?.Path,
                            },
                            stories,
                        })
                    );
                }
            },
            true
        );
        // handle drag over
        s.addEventListener(
            s.hostElement,
            'dragover',
            (e: DragEvent) => {
                const dragRow = e.dataTransfer!.getData('rowIndex');
                if (dragRow != null) {
                    _moveImage(s, s.hitTest(e));
                }
                e.preventDefault();
                e.dataTransfer!.dropEffect = 'move';
            },
            true
        );
        // handle drag drop
        s.addEventListener(
            s.hostElement,
            'drop',
            (e: DragEvent) => {
                if (this.isReadOnly() || this.isRundownDisabled()) {
                    e.preventDefault();
                    return false;
                }
                _removeImage();
                const ht = s.hitTest(e);

                const dropData = e.dataTransfer?.getData(DragEventKeys.DRAG_STORY);
                if (!dropData) {
                    return;
                }
                const data = JSON.parse(dropData);
                const { actionType } = data;

                if (ht.cellType === CellType.Cell || ht.cellType === CellType.RowHeader) {
                    if (actionType === GridDragTypes.REORDER) {
                        const { sourceRO, stories } = data;
                        const internalAction = sourceRO.Guid !== this.record().Guid ? (e.shiftKey ? GridDragTypes.MOVE : GridDragTypes.COPY) : actionType;
                        switch (internalAction) {
                            case GridDragTypes.REORDER:
                                this.reorderRowInTheCurrentGrid(s.rows[ht.row].dataItem, stories);
                                break;
                            case GridDragTypes.COPY:
                                this.moveScriptToTheCurrentGrid(ht.row, stories, sourceRO, true);
                                break;
                            case GridDragTypes.MOVE:
                                this.moveScriptToTheCurrentGrid(ht.row, stories, sourceRO);
                                break;
                        }
                    } else if (actionType === GridDragTypes.ADD) {
                        const { scriptDatabase, scriptPath, scriptGuid, text, textCommands, forceReloadStory } = data;
                        const propertiesToOverride = {};
                        const storyText = text ? text : '';
                        propertiesToOverride[PSTFieldNames.PSTDraftText] = RichTextSerializer.instance.serialize(storyText);
                        if (textCommands) {
                            const textCommandKeys = Object.keys(textCommands);
                            const textCommandValues = Object.values(textCommands);
                            if (textCommandKeys.length === textCommandValues.length && textCommandKeys.length > 0) {
                                propertiesToOverride['TextCommandKeys'] = textCommandKeys;
                                propertiesToOverride['TextCommandValues'] = textCommandValues;
                            }
                        }
                        this.addStoryToRundown.emit({ scriptDatabase, scriptPath, scriptGuid, insertBeforeGuid: s.rows[ht.row].dataItem.Guid, propertiesToOverride, forceReloadStory });
                    }

                    // e.preventDefault();
                }
            },
            true
        );

        // drop preview
        let div: HTMLDivElement;
        function _createImage(grid: FlexGrid) {
            div = document.createElement('div');
            div.className = 'wj-marker';
            grid.rowHeaders.hostElement.appendChild(div);
            return div;
        }

        function _moveImage(grid: FlexGrid, ht: HitTestInfo) {
            const slugIdx = grid.columns.findIndex(val => val.binding === PSTFieldNames.Slug);
            if (ht.cellType === CellType.Cell) {
                if (div == null) {
                    _createImage(grid);
                }
                let dropRow = ht.row;

                const range = grid.getMergedRange(grid.cells, ht.row, slugIdx);
                if (range != null) {
                    dropRow = range._row;
                }

                const rect = grid.cells.getCellBoundingRect(dropRow, 0, true);

                div.style.top = rect.top + window.scrollY + 'px';
                div.style.left = 0 + window.scrollX + 'px';
            }
        }

        function _removeImage() {
            if (div != null) {
                if (div.parentElement != null) div.parentElement!.removeChild(div);
            }
        }
    }

    public reorderRowInTheCurrentGrid = (destinationItem: StorytellingStoryRecord, stories: Partial<StorytellingStoryRecord>[] = []) => {
        // If cells have not been moved

        if (stories?.map(story => story.Guid).includes(destinationItem?.Guid)) return;
        const destGuid = destinationItem?.Guid;
        const newSequence: string[] = (this.flexGrid().rows?.map(row => row?.dataItem?.Guid) ?? []).filter(Boolean);

        // remove all guids from scriptGuids
        stories.forEach(story => {
            newSequence.splice(newSequence.indexOf(story.Guid), 1);
        });

        const newDestIdx = destGuid ? newSequence.findIndex(guid => guid === destGuid) : newSequence.length;

        // add guids from scriptGuids to the destinationItemIndex
        newSequence.splice(newDestIdx, 0, ...stories.map(story => story.Guid));

        this.collectionReordered.emit({ sequence: newSequence });

        this.clearGridSelection(this.flexGrid());
    };

    /**
     * Handles when moving a row from one grid to another, with the option to copy the script to the new grid, instead of moving it
     */
    public moveScriptToTheCurrentGrid = (rowIndex: number, stories: Partial<StorytellingStoryRecord>[], sourceRO: Partial<StorytellingRundownRecord>, copyTo = false) => {
        const destinationItemIndex = rowIndex;

        const newSequence: string[] = [];
        let insertBeforeGuid = null;

        this.flexGrid().rows.forEach((row: any, i: number) => {
            if (i === destinationItemIndex) {
                insertBeforeGuid = row.dataItem?.Guid;
                newSequence.push(...stories.map(story => story.Guid));
            }
            if (row?.dataItem) {
                newSequence.push(row.dataItem?.Guid);
            }
        });
        this.dropedScriptsToCollection.emit({ stories, insertBeforeGuid, sourceRO, copyTo });
    };

    private _selectionChangedListeners(grid: FlexGrid) {
        grid.selectionChanged.addHandler((gridparam: FlexGrid, e: CellRangeEventArgs) => {
            const slugIdx = gridparam.columns.findIndex(val => val.binding === PSTFieldNames.Slug);
            if (e.col === slugIdx) {
                const mergeRange = gridparam.getMergedRange(gridparam.cells, e.row, slugIdx);
                if (mergeRange) {
                    const selectedGuidsSet = new Set(gridparam?.selectedRows?.map(row => row?.dataItem?.Guid));

                    let allRowsSelected = true;
                    gridparam?.rows?.forEach(row => {
                        if (mergeRange._row <= row.index && mergeRange._row2 >= row.index && !selectedGuidsSet.has(row?.dataItem?.Guid)) {
                            allRowsSelected = false;
                            return true;
                        }
                    });
                    if (!allRowsSelected) {
                        grid.select(mergeRange);
                    }
                    this.selectedRows.update(_ => gridparam.selectedRows.map(item => item.dataItem));
                } else {
                    this.selectedRows.update(_ => gridparam.selectedRows.map(item => item.dataItem));
                }
            } else {
                this.selectedRows.update(_ => gridparam.selectedRows.map(item => item.dataItem));
            }
        });

        grid.selectionChanging.addHandler((s: FlexGrid, e: CellRangeEventArgs) => {
            // if the given row with the event is selected we "killing" the event with cancel=true to prevent de-selecting of row
            if (this.checkSelected(e.row, s)) {
                if (this.ctrlIsDown) {
                    e.cancel = true;
                }
            }
        });
    }
    /****************************************************************************** */

    //Ask Yanko or Ned for better approach
    @HostListener('window:keydown.control.shift.i', ['$event'])
    preventCtrlShiftI(event: Event) {
        event.preventDefault();
        return false;
    }

    @HostListener('window:keydown.control.shift.c', ['$event'])
    preventCtrlShiftC(event: Event) {
        event.preventDefault();
        return false;
    }

    private _clickListeners(s: FlexGrid) {
        s.addEventListener(s.hostElement, 'dblclick', (e: MouseEvent) => {
            const ht = s.hitTest(e);
            const column = ht.getColumn();
            if (ht.cellType === CellType.Cell && column.binding === PSTFieldNames.Actual) {
                const item = { ...s.rows[ht.row].dataItem };
                const fieldDef = [{ value: item?.Actual ?? item?.ReadTime, key: PSTFieldNames.Estimated }];
                this.store.dispatch(RecordHttpActions.saveRecordChanges({ record: item, fieldsToUpdate: fieldDef }));
            }
            e.stopPropagation();
            return false;
        });
    }

    private _keyboardListeners(s: FlexGrid) {
        s.addEventListener(
            s.hostElement,
            'keyup',
            (e: KeyboardEvent) => {
                this.ctrlIsDown = e.ctrlKey;
                if ((e.keyCode === SPACE || e.keyCode === ESCAPE) && s.activeEditor) {
                    e.stopPropagation();
                }
                if (e.keyCode === LEFT_ARROW || e.keyCode === RIGHT_ARROW) {
                    if (this.selectedRows().length > 0) {
                        e.stopPropagation();
                    }
                }
                if (e.keyCode === BACKSPACE) {
                    if (s?.selection?.isSingleCell) {
                        const row = s.selection.row;
                        const col = s.selection.col;
                        const column = s.columns[col];

                        if (column && row >= 0 && col >= 0) {
                            // TODO: This might break in the future when we decide to implement macros
                            const fieldDef = this.globalTablesService.getFieldDef(column?.binding);
                            const validationType = fieldDef?.Validation?.toUpperCase();
                            let keyCode = F2;
                            if (['COMBOBOX', 'CHECKLIST', 'STAFFIDLIST'].includes(validationType)) {
                                keyCode = F4;
                            }
                            const spaceEvent = new KeyboardEvent('keydown', {
                                bubbles: true,
                                cancelable: true,
                                keyCode,
                            });
                            s.hostElement.dispatchEvent(spaceEvent);
                            e.stopPropagation();
                            e.preventDefault();
                            return false;
                        }
                    }
                }
                if (e.keyCode === I && e.ctrlKey && e.shiftKey) {
                    if (s.selection && !this.isRundownDisabled() && !this.isRundownArchived() && this.selectedRows().length > 0) {
                        this.addRowOnCommand.emit();
                    }
                    e.stopPropagation();
                    e.preventDefault();
                    return false;
                }
                if (e.keyCode === I && e.ctrlKey) {
                    if (s.selection && !this.isRundownDisabled() && !this.isRundownArchived() && this.selectedRows().length > 0) {
                        this.addRowAbove.emit();
                    }
                    e.stopPropagation();
                    e.preventDefault();
                    return false;
                }
                if (e.keyCode === D && e.ctrlKey && e.shiftKey) {
                    if (s.selectedItems) {
                        const selectedRecords = s.selectedItems.filter(item => item.Guid !== 0);
                        if (selectedRecords.length > 0) {
                            this.store.dispatch(RecordHttpActions.deleteRecords({ records: selectedRecords }));
                        }
                    }
                    e.stopPropagation();
                    e.preventDefault();
                    return false;
                }
                if (e.keyCode === O && e.ctrlKey) {
                    if (s.selection) {
                        const storyGuid = this.flexGrid()?.rows?.[s.selection?.row]?.dataItem?.Guid;
                        if (storyGuid) {
                            this.openStoryContent.emit({ guid: storyGuid });
                        }
                    }
                    e.stopPropagation();
                    e.preventDefault();
                    return false;
                }
                if (e.keyCode === X && e.ctrlKey) {
                    if (s.selection && !s.activeEditor) {
                        this.copying(s, true);
                        e.stopPropagation();
                        e.preventDefault();
                        return false;
                    }
                }

                if (e.keyCode === X || e.keyCode === SPACE) {
                    if (s.selection && s.selection.isSingleCell) {
                        const row = s.selection.row;
                        const col = s.selection.col;
                        const column = s.columns[col];

                        if (column && row >= 0 && col >= 0) {
                            const fieldDef = this.globalTablesService.getFieldDef(column?.binding);
                            if (fieldDef?.Validation?.toUpperCase() === 'CHECKBOX') {
                                const currentRow = row;
                                const currentCol = col;
                                const dataItem = s.rows[row].dataItem;
                                const newValue = !dataItem[column.binding];

                                s.deferUpdate(() => {
                                    s.setCellData(row, col, newValue, false);
                                    s.select(new CellRange(currentRow, currentCol));
                                });

                                this.collectionChanged.emit({
                                    propID: column.binding,
                                    value: { [column.binding]: newValue },
                                    rowIndex: row,
                                    guid: dataItem.Guid,
                                });

                                e.stopPropagation();
                                e.preventDefault();
                                return false;
                            }
                        }
                    }
                }
                if (e.keyCode === ENTER || e.keyCode === ESCAPE) {
                    if (s.selection && s.selection.isSingleCell) {
                        const row = s.selection.row;
                        const col = s.selection.col;
                        const column = s.columns[col];
                        const currentRange = new CellRange(row, col);
                        s.finishEditing(e.keyCode === ENTER ? false : true);
                        if (column && row >= 0 && col >= 0) {
                            s.select(currentRange);
                            s.focus();
                        }
                    }
                    e.stopPropagation();
                    e.preventDefault();
                    return false;
                }
                if (e.keyCode === F && e.ctrlKey) {
                    this.toggleSearch.emit();
                    e.stopPropagation();
                    e.preventDefault();
                    return false;
                }
                e.preventDefault();
                return false;
            },
            true
        );

        s.addEventListener(
            s.hostElement,
            'keydown',
            (e: KeyboardEvent) => {
                if (e.keyCode === SPACE && !s.activeEditor) {
                    e.stopPropagation();
                    e.preventDefault();
                    return false;
                }

                if (e.keyCode === BACKSPACE) {
                    if (s.selection.isSingleCell && !s.activeEditor) {
                        e.stopPropagation();
                        e.preventDefault();
                    }
                }

                if (e.keyCode === ENTER || e.keyCode === ESCAPE) {
                    if (s.selection.isSingleCell) {
                        e.stopPropagation();
                        e.preventDefault();
                        return false;
                    }
                }

                if (e.keyCode === SPACE && e.shiftKey) {
                    e.stopPropagation();
                    e.preventDefault();
                    return false;
                }

                if ((e.keyCode === O && e.ctrlKey) || (e.keyCode === D && e.ctrlKey && e.shiftKey)) {
                    e.stopPropagation();
                    e.preventDefault();
                    return false;
                }

                if (e.keyCode === V && e.ctrlKey) {
                    if (s.selection && !s.activeEditor) {
                        this.pasting(s);
                        e.stopPropagation();
                        e.preventDefault();
                        return false;
                    }
                }

                if (e.keyCode === X && e.ctrlKey) {
                    if (s.selection && !s.activeEditor) {
                        e.stopPropagation();
                        e.preventDefault();
                        return false;
                    }
                }

                if (e.keyCode === C && e.ctrlKey) {
                    if (s.selection && !s.activeEditor) {
                        this.copying(s, false, e.shiftKey);
                        e.stopPropagation();
                        e.preventDefault();
                        return false;
                    }
                }

                if (e.keyCode === SPACE || e.keyCode === X) {
                    // for not loosing focus from checkbox on SPACE key or X key
                    if (s.selection && s.selection.isSingleCell) {
                        const row = s.selection.row;
                        const col = s.selection.col;
                        const column = s.columns[col];
                        if (column && row >= 0 && col >= 0) {
                            const fieldDef = this.globalTablesService.getFieldDef(column?.binding);
                            if (fieldDef?.Validation?.toUpperCase() === 'CHECKBOX') {
                                e.stopPropagation();
                                e.preventDefault();
                                return false;
                            }
                        }
                    }
                }
                if (e.keyCode === F && e.ctrlKey) {
                    e.stopPropagation();
                    e.preventDefault();
                    return false;
                }
            },
            true
        );
    }

    /**
     * Checks the given row if exists in selected rows array
     * @param row row number
     * @param grid the grid component
     * @returns  true or false
     */
    checkSelected(row: number, grid: FlexGrid): any {
        let result = false;
        grid.selectedRows.forEach(val => {
            if (row === val._idx) {
                result = true;
            }
        });
        return result;
    }

    private clearGridSelection(flexGrid: FlexGrid): void {
        if (flexGrid) {
            flexGrid.select(-1, -1);
        }
    }

    private applyRowFlashing(newItemsSource: StorytellingStoryRecord[]) {
        newItemsSource.forEach((row, index) => {
            if (row?.isRowFlashing) {
                for (let i = 0; i < this.flexGrid()?.columns?.length; i++) {
                    const cell = this.flexGrid()?.cells?.getCellElement(index, i);
                    addClass(cell, 'flash');
                }
            } else {
                for (let i = 0; i < this.flexGrid()?.columns?.length; i++) {
                    const cell = this.flexGrid()?.cells?.getCellElement(index, i);
                    removeClass(cell, 'flash');
                }
            }
        });
    }

    findColumnIndex(columnName: string): number {
        const column = this.flexGrid().getColumn(columnName);
        return column ? this.flexGrid().columns.indexOf(column) : -1;
    }

    isStoryLockedByMe(story: StorytellingStoryRecord): boolean {
        return story?.LockedBy === this.globalTablesService?.nomWebServicesJson?.HelloResults?.UserID;
    }

    async copying(grid: FlexGrid<StorytellingStoryRecord>, isCutOperation: boolean, copyRow?: boolean) {
        if (copyRow) {
            const copyData: RundownCopyPasteArgs = {
                sourceRO: { Guid: this.record()?.Guid, Database: this.record()?.Database, Path: this.record()?.Path },
                stories: grid.selectedItems.map((item: StorytellingStoryRecord) => {
                    return {
                        Guid: item.Guid,
                        Database: item.Database,
                        Path: item.Path,
                        Slug: item?.Slug,
                        Segment: item?.Segment,
                        Approved: item?.Approved,
                    };
                }),
                isCutOperation: isCutOperation,
            };
            await navigator.clipboard.writeText(JSON.stringify(copyData));
        } else {
            await navigator.clipboard.writeText(grid.getCellData(grid.selection.row, grid.selection.col, true));
        }
    }

    async pasting(grid: FlexGrid) {
        let copiedData: RundownCopyPasteArgs = null;
        const clipboardString = await navigator.clipboard.readText();
        try {
            copiedData = JSON.parse(clipboardString);
        } catch (_e) {
            // not a JSON, probably copying a cell value
        }

        if (typeof copiedData !== 'string' && grid.selection && copiedData && copiedData?.stories?.length > 0) {
            const destinationItemIndex = grid.selection.row;
            const col = grid.selection.col;
            const copiedROGuid = copiedData.sourceRO.Guid;

            if (copiedROGuid === this.record().Guid && (!this.record()?.DupeSlugs || (this.record()?.DupeSlugs && copiedData.isCutOperation))) {
                this.reorderRowInTheCurrentGrid(grid.rows[grid.selection.row].dataItem, copiedData.stories);
            } else {
                this.moveScriptToTheCurrentGrid(destinationItemIndex, copiedData.stories, copiedData.sourceRO, true);
            }

            grid.select(new CellRange(destinationItemIndex, col, destinationItemIndex, col));
        } else {
            this.pastingCell(grid, clipboardString);
        }
    }

    pastingCell(grid: FlexGrid, event: string) {
        if (grid.startEditing(false, grid.selection.row, grid.selection.col)) {
            grid.activeEditor.value = event;
            grid.finishEditing();
        }
    }

    onSearchTermChanged(event: { searchText: string }) {
        this.searchTerm.set(event?.searchText);
    }

    executeSearch() {
        this.clearPreviousMarkSearch();

        if (!this.searchTerm() || this.searchTerm().trim() === '') {
            this.clearPreviousSearch(null);
            return;
        }

        const searchLower = this.searchTerm().toLowerCase();

        // Don't re-search if the term hasn't changed and there is a match
        if (searchLower === this.previousSearch && this.matches.length > 0 && this.matches[this.currentMatchIndex]) {
            this.navigate({ direction: NavigationDirection.NEXT });
            return;
        }

        this.clearPreviousSearch(searchLower);
        this.performSearch();
        this.setStartingIndex();
        this.previousSearch = searchLower;

        if (this.matches.length > 0) {
            this.navigate({ direction: NavigationDirection.NEXT });
        }
    }

    setStartingIndex() {
        const grid = this.flexGrid();
        if (this.matches.length > 0) {
            const currentSelection = grid.selection;

            if (currentSelection && currentSelection.row >= 0 && currentSelection.col >= 0) {
                const selRow = currentSelection.row;
                const selCol = currentSelection.col;
                const selectedCellMatchIndex = this.matches.findIndex(match => match.row > selRow || (match.row === selRow && match.col >= selCol));

                if (selectedCellMatchIndex >= 0) {
                    this.currentMatchIndex = selectedCellMatchIndex - 1;
                    return;
                }
            }
            this.currentMatchIndex = -1;
        }
    }

    performSearch() {
        this.matches = [];
        const searchLower = this.searchTerm().toLowerCase();
        if (this.colPageNum === -1) {
            this.colPageNum = this.flexGrid()?.columns?.findIndex(c => c.binding === PSTFieldNames.PageNum);
        }

        for (let r = 0; r < this.flexGrid().rows.length; r++) {
            if (!this.flexGrid().rows[r]?.dataItem?.Guid || this.flexGrid().rows[r]?.dataItem?.Guid === BLACK_LINE_GUID) {
                continue;
            }
            for (let c = 0; c < this.flexGrid().columns.length; c++) {
                if (c === this.colPageNum) {
                    const storyText = this.flexGrid().rows[r]?.dataItem?.Text?.replace(/<[^>]*>/g, '');
                    const storyTextLower = storyText?.toLowerCase() || '';

                    if (storyTextLower.includes(searchLower)) {
                        this.matches.push({
                            row: r,
                            col: this.colPageNum,
                        });
                        continue;
                    }
                }
                const cellText = this.parseMatchesByValueType(r, c);
                if (cellText) {
                    const plainText = cellText?.replace(/<[^>]*>/g, '');
                    const plainTextLower = plainText?.toLowerCase() || '';

                    if (plainTextLower.includes(searchLower)) {
                        this.matches.push({
                            row: r,
                            col: c,
                        });
                    }
                }
            }
        }
    }

    private applySearchHighlightingToCell(rowIndex: number, colIndex: number, cell: HTMLElement, rowDataItem: any, column: any): void {
        // Skip search highlighting if search is not active, there is no search term, no data item, or if this is the delete column
        if (!this?.isSearchActive() || !this.searchTerm() || !rowDataItem) return;
        if (column.name === 'delete-column') return;
        if (this.currentMatchIndex === -1 || !this.matches?.length || !this.matches[this.currentMatchIndex]) return;

        // Ensure we're not in edit mode - this is critical to prevent HTML tags showing in editors
        if (this.flexGrid().editRange && this.flexGrid().editRange.contains(rowIndex, colIndex)) {
            return;
        }

        const parsedText = this.parseMatchesByValueType(rowIndex, colIndex);
        if (!parsedText) return;

        const validation = this.gridLayout()?.columns.find(c => c.name === column.binding)?.validation;

        // Determine if this cell is the currently matched cell that should be highlighted
        const isActiveMatch = this.matches[this.currentMatchIndex].row === rowIndex && this.matches[this.currentMatchIndex].col === colIndex;
        const highlightedHtml = isActiveMatch && validation !== FieldDefValidations.CHECKBOX ? this.highlightText(parsedText, rowIndex, colIndex) : parsedText;

        if (colIndex === this.colPageNum) {
            this.applyPageNumHighlight(rowIndex);
            return;
        }
        // Never modify the data model with HTML - only update the visual representation
        switch (column.binding) {
            case PSTFieldNames.Segment: {
                const valueSpan = cell.querySelector('.segment-column__value');
                if (valueSpan) {
                    valueSpan.innerHTML = highlightedHtml;
                } else {
                    cell.innerHTML = highlightedHtml;
                }
                break;
            }
            case PSTFieldNames.MOSStatus:
            case PSTFieldNames.MOSChannels:
            case PSTFieldNames.ObjectAutoCreate:
            case PSTFieldNames.ObjectPlaceholderCreate: {
                const valueSpan = cell.querySelector('.object-placeholder-create-value');
                if (valueSpan) {
                    valueSpan.innerHTML = highlightedHtml;
                } else {
                    cell.innerHTML = highlightedHtml;
                }
                break;
            }
            default:
                switch (validation) {
                    case FieldDefValidations.SPECIAL:
                    case FieldDefValidations.CHECKBOX: {
                        if (isActiveMatch) {
                            this.applyPageNumHighlight(rowIndex);
                        }
                        return;
                    }
                    default:
                        cell.innerHTML = highlightedHtml;
                        break;
                }
        }

        // After highlighting the matched cell (or clearing a non-match), update PageNum for this row
        if (isActiveMatch) {
            this.applyPageNumHighlight(rowIndex);
        }
    }

    private highlightCell() {
        const rowIndex = this.matches[this.currentMatchIndex]?.row;
        const colIndex = this.matches[this.currentMatchIndex]?.col;

        // Trigger a refresh when the data is not yet loaded (when it is selecting data that was not in the visible grid area)
        if (!this.flexGrid().cells.getCellElement(rowIndex, colIndex)) {
            this.flexGrid().invalidate();
        }

        const cell = this.flexGrid().cells.getCellElement(rowIndex, colIndex);
        if (!cell) {
            return;
        }

        const rowDataItem = this.flexGrid().rows[rowIndex].dataItem;
        const column = this.flexGrid().columns[colIndex];

        this.applySearchHighlightingToCell(rowIndex, colIndex, cell, rowDataItem, column);
    }

    navigate(event: { direction: NavigationDirection }) {
        if (this.matches.length === 0 || this.previousSearch !== this.searchTerm().toLowerCase()) {
            this.executeSearch();
            return;
        }

        this.clearPreviousMarkSearch();
        this.handleSearchRefresh();

        if (event?.direction === NavigationDirection.NEXT) {
            this.currentMatchIndex = this.currentMatchIndex >= this.matches.length - 1 ? 0 : this.currentMatchIndex + 1;
        } else {
            this.currentMatchIndex = this.currentMatchIndex <= 0 ? this.matches.length - 1 : this.currentMatchIndex - 1;
        }

        this.navigateToMatch();
        this.highlightCell();
        this.searchInputComponent()?.searchInput()?.focus();
    }

    navigateToMatch() {
        const grid = this.flexGrid();
        const match = this.matches[this.currentMatchIndex];
        if (!match) {
            return;
        }

        // Refresh selection in order to change cell if there are a few matches at the same row
        grid.select(-1, -1);

        // Focus on the cell
        grid.select(match.row, match.col);
    }

    handleSearchRefresh() {
        if (this.searchAgain) {
            if (this.currentMatchIndex !== -1) {
                this.navigateToMatch();
                this.performSearch();
                this.setStartingIndex();
                this.currentMatchIndex++;
            }
            this.searchAgain = false;
        }
    }

    parseMatchesByValueType(row: number, col: number) {
        const grid = this.flexGrid();
        let cellValue = '';
        const cell = grid.cells.getCellElement(row, col);
        const column = grid?.columns[col];
        const item = grid.rows[row].dataItem;
        const value = item ? item[column.binding] : null;
        const validation = this.gridLayout()?.columns.find(c => c.name === column.binding)?.validation;

        // Create a context object that matches ICellTemplateContext
        const context: ICellTemplateContext = {
            row: grid.rows[row],
            col: grid.columns[col],
            item: item,
            value: value,
            text: cell?.textContent || value?.toString() || '',
        };

        switch (column.binding) {
            case PSTFieldNames.MOSStatus:
                cellValue = this.mosStatusCellTemplate(context);
                break;
            case PSTFieldNames.MOSChannels:
                cellValue = cell?.textContent || value?.toString() || '';
                break;
            case PSTFieldNames.ObjectAutoCreate:
                cellValue = this.objectAutoCreateCellTemplate(context);
                break;
            case PSTFieldNames.ObjectPlaceholderCreate:
                cellValue = item?.ObjectPlaceholderCreate || '';
                break;
            case PSTFieldNames.Slug:
                cellValue = item?.Slug || '';
                break;
            case PSTFieldNames.Segment:
                cellValue = item?.Segment || '';
                break;
            default:
                switch (validation) {
                    case FieldDefValidations.COMBOBOX:
                        cellValue = this.comboBoxCellTemplate(context);
                        break;
                    case FieldDefValidations.CHECKBOX:
                        cellValue = value?.toString() || '';
                        break;
                    case FieldDefValidations.CHECKLIST:
                        cellValue = this.checkListCellTemplate(context);
                        break;
                    case FieldDefValidations.STAFFIDLIST:
                        cellValue = this.staffIdListCellTemplate(context);
                        break;
                    case FieldDefValidations.DURATION:
                        cellValue = this.durationCellTemplate(context);
                        break;
                    case FieldDefValidations.TIME:
                        cellValue = this.timeCellTemplate(context);
                        break;
                    case FieldDefValidations.DATE:
                        cellValue = this.dateCellTemplate(context);
                        break;
                    case FieldDefValidations.MOSDATETIME:
                        cellValue = this.timeCellTemplate(context);
                        break;
                    default:
                        cellValue = cell?.textContent || value?.toString() || '';
                }
                break;
        }

        return cellValue;
    }

    highlightText(value: string, cellRow: number, cellCol: number) {
        if (!value) {
            return '';
        }

        const currentMatch = this.matches[this.currentMatchIndex];
        if (!currentMatch) return;
        if (currentMatch.row === cellRow && currentMatch.col === cellCol) {
            const valueLC = value.toLowerCase();
            const searchLC = this.searchTerm()?.toLowerCase();

            if (this.searchTerm() && valueLC?.includes(searchLC)) {
                const matchIndex = valueLC.indexOf(searchLC);
                const textBeforeMatch = value.substring(0, matchIndex);
                const match = value.substring(matchIndex, matchIndex + this.searchTerm().length);
                const textAfterMatch = value.substring(matchIndex + this.searchTerm().length);

                return `${textBeforeMatch}<mark>${match}</mark>${textAfterMatch}`;
            }
        }
        return value;
    }

    clearPreviousMarkSearch() {
        if (this.matches.length > 0) {
            const grid = this.flexGrid();
            const match = this.matches[this.currentMatchIndex];
            if (this.currentMatchIndex >= 0 && match) {
                const cell = grid.cells.getCellElement(match.row, match.col);
                const selectors = ['.segment-column__value', '.object-placeholder-create-value'];
                if (!cell) {
                    return;
                }
                const element = selectors.map(selector => cell.querySelector(selector as string)).find(el => el !== null) || cell;
                if (element && element.innerHTML?.includes('<mark>')) {
                    element.innerHTML = element.innerHTML.replace(/<mark>|<\/?mark>/g, '');
                }

                const pageNumCell = this.flexGrid().cells.getCellElement(this.matches[this.currentMatchIndex].row, this.colPageNum);
                if (pageNumCell) {
                    pageNumCell.innerHTML = pageNumCell.innerHTML.replace(/<mark>|<\/?mark>/g, '');
                }
            }
        }
    }

    private applyPageNumHighlight(rowIndex: number): void {
        this.colPageNum = this.flexGrid().columns.findIndex(c => c.binding === PSTFieldNames.PageNum);
        if (!this.flexGrid() || this.colPageNum === -1) return;

        const pageNumCell = this.flexGrid().cells.getCellElement(rowIndex, this.colPageNum);
        if (!pageNumCell) return;

        // Skip if the PageNum cell is being edited
        if (this.flexGrid().editRange && this.flexGrid().editRange.contains(rowIndex, this.colPageNum)) {
            return;
        }

        const pageNumText = pageNumCell.textContent || '';
        pageNumCell.innerHTML = `<mark>${pageNumText}</mark>`;
    }

    clearPreviousSearch(previousSearch: string | null) {
        this.matches = [];
        this.previousSearch = previousSearch;
    }
}
